import { useContext, useState } from 'react';
import { RadioField, Section, SelectField } from '@dispatch/web/ui';
import { Grid, Divider, Stack, Box } from '@mui/material';
import {
  Actions,
  CompanyRole,
  Group,
  MultiSelectOption,
  Office,
  ResourceForm,
  Subjects,
  Team,
} from '@dispatch/shared/types';
import {
  Control,
  FieldValues,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';
import { ResourceContext } from '../../ResourcesDetails';
import { Ability } from '@casl/ability';

export type TeamsOfficesRolesProp = {
  errors: FieldErrors;
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceForm>;
  isReadOnly?: boolean;
};

export const TeamsOfficesRoles = ({
  errors,
  control,
  permissions,
  watch,
  isReadOnly = false,
}: TeamsOfficesRolesProp) => {
  const { data } = useContext(ResourceContext);
  const roles = data?.company?.role?.map(
    (role: CompanyRole): MultiSelectOption => {
      return {
        title: role.name || '',
        value: role.integrationGUID,
      };
    }
  );
  const teams = data?.company?.team?.map((team: Team): MultiSelectOption => {
    return {
      title: team.name,
      value: team.integrationGUID,
    };
  });

  const groups = data?.company?.group?.map((team: Group): MultiSelectOption => {
    return {
      title: team.name,
      value: team.integrationGUID,
    };
  });

  const offices = data?.company?.office?.map(
    (office: Office): MultiSelectOption => {
      return {
        title: office.name,
        value: office.integrationGUID,
      };
    }
  );

  const firstName = data?.name?.split(' ')[0];
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const [showDropDown, setShowDropDown] = useState(data?.isContingent);

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Stack>
        <Box hidden={!hasUpsertPermissions}>
          <Section>
            <Grid container>
              <Grid spacing={2} marginBottom={2} size={4}>
                <RadioField
                  row
                  control={control}
                  id={'isContingent'}
                  name={'teamsOfficesRoles.isContingent'}
                  buttons={[
                    {
                      label: 'Staff',
                      value: false,
                    },
                    {
                      label: 'Contingent',
                      value: true,
                    },
                  ]}
                  onClick={(value) => setShowDropDown(!!value)}
                  disabled={isReadOnly}
                />
                <Box hidden={!showDropDown}>
                  <SelectField
                    id={'contingentCompany'}
                    name={'teamsOfficesRoles.contingentCompany'}
                    label={'Company'}
                    control={control}
                    errors={errors}
                    options={[
                      {
                        title: 'Pilot',
                        value: 'pilot',
                      },
                      {
                        title: 'Crawford',
                        value: 'crawford',
                      },
                      {
                        title: 'Other',
                        value: 'other',
                      },
                    ]}
                    required
                    disabled={isReadOnly}
                  />
                </Box>
              </Grid>
            </Grid>
          </Section>

          <Divider sx={{ margin: '0.5rem 0 1.375rem 0' }} />
        </Box>

        <Section title={firstName ? `${firstName}'s Teams` : 'Teams'}>
          <Grid container>
            <Grid container spacing={2} marginBottom={2}>
              <Grid data-test-id="teams-dropdown" size={12}>
                <SelectField
                  multiple
                  id="teams"
                  name={'teamsOfficesRoles.teams'}
                  label="Teams"
                  control={control}
                  errors={errors}
                  options={teams || []}
                  readOnly={!hasUpsertPermissions}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
          </Grid>
        </Section>

        <Divider sx={{ margin: '0.5rem 0 1.375rem 0' }} />

        <Section title={firstName ? `${firstName}'s Offices` : 'Offices'}>
          <Grid container>
            <Grid container spacing={2} marginBottom={2}>
              <Grid data-test-id="offices-dropdown" size={12}>
                <SelectField
                  multiple
                  id="offices"
                  name={'teamsOfficesRoles.offices'}
                  label="Offices"
                  control={control}
                  errors={errors}
                  options={offices || []}
                  readOnly={!hasUpsertPermissions}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
          </Grid>
        </Section>
        <Divider sx={{ margin: '0.5rem 0 1.375rem 0' }} />
        <Section title={firstName ? `${firstName}'s Roles` : 'Roles'}>
          <Grid container>
            <Grid container spacing={2} marginBottom={2}>
              <Grid data-test-id="roles-dropdown" size={12}>
                <SelectField
                  multiple
                  id="roles"
                  name={'teamsOfficesRoles.roles'}
                  label="Roles"
                  control={control}
                  errors={errors}
                  options={roles || []}
                  readOnly={!hasUpsertPermissions}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
          </Grid>
        </Section>
        <Section title={firstName ? `${firstName}'s Groups` : 'Groups'}>
          <Grid container>
            <Grid container spacing={2} marginBottom={2}>
              <Grid data-test-id="groups-dropdown" size={12}>
                <SelectField
                  multiple
                  id="groups"
                  name={'teamsOfficesRoles.groups'}
                  label="Groups"
                  control={control}
                  errors={errors}
                  options={groups || []}
                  readOnly={!hasUpsertPermissions}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Stack>
    </div>
  );
};
